import { toast } from 'sonner';
import { getPrefs } from './prefs';
import { withBackend } from './api';
import { getDefaultModel } from './models';

// Optional pre-generation cost gate (Settings -> AI & Generation).
// Fails OPEN everywhere: billing off, quote unreachable, zero-cost action —
// the feature must never be able to block generation by breaking.

const GEN_TYPE = {
  presentation: 'presentation', infographic: 'infographic',
  document: 'document', dashboard: 'dashboard',
  sheets: 'sheet', sheet: 'sheet',
};
// Mirrors backend classify_model_tier.
const MODEL_TIER = {
  flash: 'flash', pro: 'pro', sonnet: 'pro', kimi: 'pro',
  opus: 'opus', sol: 'opus', gpt: 'opus', gpt55: 'opus',
  astra: 'ultra', // r178
};

// Single-flight: while one confirm is on screen, further calls resolve
// false instead of stacking a second Infinity-duration toast whose two
// "Generate" buttons could start two generations (r88 review).
let _pending = false;

/**
 * When the user enabled cost confirmation, fetch a quote and ask before
 * proceeding. Resolves true to proceed, false to cancel.
 */
export async function maybeConfirmCost({ projectType, model, items = 1, userId }) {
  if (!getPrefs().confirmCost) return true;
  if (_pending) return false;
  let q = null;
  try {
    const resp = await fetch(withBackend('/api/billing/quote'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        generation_type: GEN_TYPE[projectType] || 'presentation',
        model_tier: MODEL_TIER[model || getDefaultModel()] || 'flash',
        item_count: Math.max(1, items || 1),
        user_id: userId || '',
      }),
    });
    if (!resp.ok) return true;
    q = await resp.json();
  } catch {
    return true;
  }
  if (!q || q.error) return true;
  // r177: unmetered accounts (Prezlab staff bypass every tier gate) have no
  // spend to confirm — the dialog used to show a false "Balance: 0".
  if (q.metered === false) return true;

  const parts = [];
  if (q.total_credits > 0) parts.push(`${q.total_credits} credit${q.total_credits === 1 ? '' : 's'}`);
  if (q.approx_tokens > 0) parts.push(`~${Math.max(1, Math.round(q.approx_tokens / 1000))}k tokens`);
  if (!parts.length) return true;
  const balanceNote = q.balance != null ? ` · Balance: ${q.balance}` : '';

  _pending = true;
  return new Promise((resolve) => {
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; _pending = false; resolve(v); } };
    toast(`Estimated: ${parts.join(' · ')}${balanceNote}`, {
      id: 'wiz-cost-confirm',
      description: 'Generate now? You can turn this check off in Settings → AI & Generation.',
      duration: Infinity,
      action: { label: 'Generate', onClick: () => done(true) },
      cancel: { label: 'Cancel', onClick: () => done(false) },
      onDismiss: () => done(false),
    });
  });
}
